import { useEffect, useRef, useState, useMemo, useLayoutEffect } from "react";
import { useI18n } from "../../lib/i18n.jsx";
import { Sun, Sunset, Moon, Coffee, Activity, Utensils, Sparkles, Tv } from "lucide-react";

// Per-hour photo background — recycle existing assets so we don't ship more files.
const IMAGE_FOR_INDEX = [
  "/img/schedule/breakfast.webp",        // 08:00 wake-up & breakfast
  "/img/schedule/session.webp",         // 09:30 session 1 · coached
  "/img/schedule/activity.webp",         // 12:30 lunch & chill
  "/img/schedule/learning.webp",   // 14:00 daily attraction
  "/img/schedule/dinner.webp", // 17:00 session 2 · free ride
  "/img/schedule/night_session.webp",      // 19:00 dinner
  "/img/schedule/goodnight.webp"       // 20:30 evening theme
];

// Map of icon name -> component so items can supply a string key.
const ICONS = { Sun, Sunset, Moon, Coffee, Activity, Utensils, Sparkles, Tv };
const ICON_FOR_HOUR = (hour) => {
  if (hour < 9) return Coffee;
  if (hour < 12) return Activity;
  if (hour < 14) return Utensils;
  if (hour < 17) return Sparkles;
  if (hour < 19) return Sun;
  if (hour < 20) return Utensils;
  return Tv;
};

const PERIOD_ICON = (hour) => {
  if (hour < 12) return Sun;
  if (hour < 19) return Sun;
  if (hour < 21) return Sunset;
  return Moon;
};

export default function Schedule() {
  const { t, lang } = useI18n();
  const items = t.schedule.items;
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState({ left: 0, width: 0, lineLeft: 0, lineRight: 0, top: 20 });
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const innerRef = useRef(null);

  const hours = useMemo(
    () => items.map((it) => parseInt(it.time.split(":")[0], 10) || 0),
    [items]
  );

  // Auto-advance every 5s when section is visible & not paused.
  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    let visible = false;
    let timer = null;
    const obs = new IntersectionObserver(
      (entries) => {
        visible = entries.some((e) => e.isIntersecting && e.intersectionRatio >= 0.3);
        if (visible && !paused && !timer) {
          timer = setInterval(() => setActive((a) => (a + 1) % items.length), 5000);
        } else if ((!visible || paused) && timer) {
          clearInterval(timer);
          timer = null;
        }
      },
      { threshold: [0, 0.3, 0.7] }
    );
    obs.observe(node);
    return () => {
      obs.disconnect();
      if (timer) clearInterval(timer);
    };
  }, [items.length, paused]);

  // Recompute progress-bar geometry from real DOM positions of dots so the
  // line aligns with the centre of the first dot → centre of the active dot.
  // Use getBoundingClientRect with the INNER positioning container as the
  // reference (since the line is absolutely positioned inside it). Walking
  // offsetParent up to the outer track was incorrect because mx-auto on the
  // inner div introduces a horizontal offset that broke the alignment.
  useLayoutEffect(() => {
    const track = trackRef.current;
    const inner = innerRef.current;
    if (!track || !inner) return;
    const recompute = () => {
      const first = inner.querySelector('[data-dot-idx="0"] [data-dot-circle]');
      const last = inner.querySelector(
        `[data-dot-idx="${items.length - 1}"] [data-dot-circle]`
      );
      const cur = inner.querySelector(`[data-dot-idx="${active}"] [data-dot-circle]`);
      if (!first || !last || !cur) return;
      const innerRect = inner.getBoundingClientRect();
      const firstRect = first.getBoundingClientRect();
      const lastRect = last.getBoundingClientRect();
      const curRect = cur.getBoundingClientRect();
      const firstCx = firstRect.left + firstRect.width / 2 - innerRect.left;
      const lastCx = lastRect.left + lastRect.width / 2 - innerRect.left;
      const curCx = curRect.left + curRect.width / 2 - innerRect.left;
      const dotCy = firstRect.top + firstRect.height / 2 - innerRect.top;
      setProgress({
        lineLeft: firstCx,
        lineRight: lastCx,
        left: firstCx,
        width: Math.max(0, curCx - firstCx),
        top: dotCy,
      });
    };
    // Run twice — once now, once after fonts/icons settle, to handle reflow.
    recompute();
    const raf = requestAnimationFrame(recompute);
    const ro = new ResizeObserver(recompute);
    ro.observe(inner);
    ro.observe(track);
    window.addEventListener("resize", recompute);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("resize", recompute);
    };
  }, [active, items.length]);

  // Keep active dot horizontally in view on the track (mobile scroll).
  // Skip the very first render so we don't yank the page on initial mount.
  const initialMount = useRef(true);
  useEffect(() => {
    if (initialMount.current) {
      initialMount.current = false;
      return;
    }
    const track = trackRef.current;
    if (!track) return;
    const el = track.querySelector(`[data-dot-idx="${active}"]`);
    if (!el) return;
    // Use the inner scroll container only — don't propagate the scroll up
    // to the page (which previously auto-scrolled the section into view).
    const dotRect = el.getBoundingClientRect();
    const trackRect = track.getBoundingClientRect();
    const dotCx = dotRect.left + dotRect.width / 2;
    const trackCx = trackRect.left + trackRect.width / 2;
    track.scrollBy({ left: dotCx - trackCx, behavior: "smooth" });
  }, [active]);

  const activeItem = items[active];
  const activeHour = hours[active];
  const ActiveIcon = activeItem && activeItem.icon
    ? (typeof activeItem.icon === "string" ? ICONS[activeItem.icon] || ICON_FOR_HOUR(activeHour) : activeItem.icon)
    : ICON_FOR_HOUR(activeHour);
  const PeriodIcon = PERIOD_ICON(activeHour);
  const activeImage = IMAGE_FOR_INDEX[active] || IMAGE_FOR_INDEX[0];

  return (
    <section
      ref={sectionRef}
      data-testid="schedule-section"
      className="bg-cream py-20 md:py-28"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="max-w-[1100px] mx-auto px-5 md:px-8">
        <div className="text-center mb-12">
          <p className="font-handwritten text-summer text-2xl md:text-3xl mb-2">
            {t.schedule.kicker}
          </p>
          <h2
            className="font-display font-bold text-winter leading-tight"
            style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)" }}
          >
            {t.schedule.title}
          </h2>
        </div>

        {/* Active card with photo background */}
        <div
          data-testid="schedule-active-card"
          className="relative flex items-center rounded-3xl shadow-[0_15px_40px_-15px_rgba(27,41,73,0.25)] mb-8 overflow-hidden min-h-[220px] md:min-h-[260px]"
        >
          {/* Background image (preload all so swap is instant after first paint) */}
          {IMAGE_FOR_INDEX.map((src, i) => (
            <img
              key={src}
              src={src}
              alt=""
              aria-hidden="true"
              loading={i === 0 ? "eager" : "lazy"}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === active ? "opacity-100" : "opacity-0"
                }`}
            />
          ))}
          {/* Gradient overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-winter/85 via-winter/70 to-winter/40" />

          {/* Content — single column, top-aligned */}
          <div className="relative z-10 w-full p-6 md:p-9 text-cream max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 md:w-12 md:h-12 rounded-xl bg-cream shadow-lg flex items-center justify-center shrink-0">
                <ActiveIcon className="w-5 h-5 md:w-6 md:h-6 text-summer" strokeWidth={2.2} />
              </div>
              <div className="flex flex-col leading-tight">
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-cream/75 inline-flex items-center gap-1.5">
                  <PeriodIcon className="w-3 h-3" />
                  {(() => {
                    const PERIODS = {
                      pl: ["rano", "po południu", "wieczorem", "noc"],
                      uk: ["ранок", "після обіду", "ввечері", "ніч"],
                      en: ["morning", "afternoon", "evening", "night"],
                    };
                    const arr = PERIODS[lang] || PERIODS.en;
                    return activeHour < 12 ? arr[0] : activeHour < 17 ? arr[1] : activeHour < 21 ? arr[2] : arr[3];
                  })()}
                </span>
                <span
                  className="font-display font-extrabold text-cream tabular-nums drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)] mt-0.5"
                  style={{ fontSize: "clamp(1.5rem, 2.6vw, 2rem)" }}
                >
                  {activeItem.time}
                </span>
              </div>
            </div>
            <h3
              key={active}
              className="font-display font-bold text-cream mb-2 leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)]"
              style={{ fontSize: "clamp(1.75rem, 3.2vw, 2.5rem)" }}
            >
              {activeItem.title}
            </h3>
            <p
              key={`b-${active}`}
              className="text-cream/90 text-base md:text-lg leading-relaxed drop-shadow-[0_1px_4px_rgba(0,0,0,0.3)] max-w-xl"
            >
              {activeItem.body}
            </p>
          </div>
        </div>

        {/* Horizontal timeline track */}
        <div
          ref={trackRef}
          className="relative overflow-x-auto overflow-y-visible py-3 scrollbar-hidden"
          data-testid="schedule-track"
        >
          <div ref={innerRef} className="relative w-max mx-auto">
            {/* Connecting line — anchored to first/last dot centres, vertically centred through the dot row */}
            <div
              className="absolute h-[3px] rounded-full bg-winter/15"
              style={{
                top: `${progress.top - 1.5}px`,
                left: `${progress.lineLeft}px`,
                width: `${Math.max(0, progress.lineRight - progress.lineLeft)}px`,
              }}
            />
            {/* Active line up to current dot */}
            <div
              className="absolute h-[3px] rounded-full bg-summer transition-all duration-500"
              style={{
                top: `${progress.top - 1.5}px`,
                left: `${progress.left}px`,
                width: `${progress.width}px`,
              }}
            />

            <div className="relative flex gap-4 sm:gap-8 md:gap-12 px-2">
              {items.map((it, i) => {
                const isActive = i === active;
                const Icon = it && it.icon
                  ? (typeof it.icon === "string" ? ICONS[it.icon] || ICON_FOR_HOUR(hours[i]) : it.icon)
                  : ICON_FOR_HOUR(hours[i]);
                return (
                  <button
                    key={i}
                    type="button"
                    data-dot-idx={i}
                    data-testid={`schedule-dot-${i}`}
                    onClick={() => setActive(i)}
                    className="flex flex-col items-center gap-2 group min-w-[68px] focus:outline-none"
                    aria-label={`${it.time} – ${it.title}`}
                    aria-current={isActive ? "true" : "false"}
                  >
                    <span
                      data-dot-circle
                      className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${isActive
                          ? "bg-summer text-cream ring-4 ring-summer/20 shadow-[0_6px_18px_-4px_rgba(48,170,76,0.55)]"
                          : "bg-cream text-winter/55 ring-2 ring-winter/15 group-hover:ring-summer/50 group-hover:text-summer"
                        }`}
                    >
                      <Icon className="w-[18px] h-[18px]" strokeWidth={2.2} />
                    </span>
                    <span
                      className={`font-mono font-bold text-[11px] tabular-nums whitespace-nowrap transition-colors ${isActive ? "text-summer" : "text-winter/55"
                        }`}
                    >
                      {it.time}
                    </span>
                    <span
                      className={`text-[10px] font-semibold whitespace-nowrap transition-colors hidden sm:block max-w-[120px] truncate ${isActive ? "text-winter" : "text-winter/45"
                        }`}
                    >
                      {it.title}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
